/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance"; // token JWT incluido automáticamente
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";

const WEEKDAY_OPTIONS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const emptyWeekdayValues = () =>
  WEEKDAY_OPTIONS.reduce((acc, day) => {
    acc[day.key] = "";
    return acc;
  }, {});

const normalizeWeekdayPayload = (source) => {
  const normalized = {};
  let hasAny = false;

  for (const day of WEEKDAY_OPTIONS) {
    const raw = source?.[day.key];
    if (raw === "" || raw === null || typeof raw === "undefined") {
      normalized[day.key] = null;
      continue;
    }

    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1) {
      return { value: null, error: `${day.label}: value must be an integer >= 1` };
    }

    normalized[day.key] = n;
    hasAny = true;
  }

  return { value: hasAny ? normalized : null, error: null };
};

const validateStayRanges = ({ minDefault, maxDefault, minByWeekday, maxByWeekday }) => {
  if (minDefault !== null && maxDefault !== null && minDefault > maxDefault) {
    return "Default Min Stay cannot be greater than Default Max Stay.";
  }

  for (const day of WEEKDAY_OPTIONS) {
    const minV = minByWeekday?.[day.key] ?? null;
    const maxV = maxByWeekday?.[day.key] ?? null;
    if (minV !== null && maxV !== null && minV > maxV) {
      return `${day.label}: Default Min Stay cannot be greater than Default Max Stay.`;
    }
  }

  return null;
};

export const getRoomRateFormPrecisionError = (formData, currencyCatalog = []) => {
  const currency = String(formData?.currency || "").trim().toUpperCase();
  const minorUnits = getCurrencyMinorUnits(currency, currencyCatalog);
  const invalidPrice = [formData?.min_price_amount, formData?.max_price_amount].some(
    (value) => value !== "" && value !== null && value !== undefined &&
      !hasValidCurrencyPrecision(value, currency, currencyCatalog)
  );
  if (invalidPrice) {
    return `${currency} price limits accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
  }
  if (
    formData?.penalty_type === "fixed" &&
    formData?.penalty_amount !== "" &&
    formData?.penalty_amount !== null &&
    formData?.penalty_amount !== undefined &&
    !hasValidCurrencyPrecision(formData.penalty_amount, currency, currencyCatalog)
  ) {
    return `${currency} fixed penalties accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
  }
  return null;
};

const formShellStyle = {
  width: "100%",
  maxWidth: "960px",
  margin: "0 auto",
  borderRadius: "22px",
  padding: "28px",
  background:
    "linear-gradient(180deg, rgba(10, 10, 18, 0.95), rgba(8, 13, 25, 0.95)) padding-box, linear-gradient(170deg, rgba(61, 182, 233, 0.35), rgba(34, 58, 122, 0.25)) border-box",
  border: "1px solid rgba(148, 163, 184, 0.3)",
  boxShadow: "0 30px 60px rgba(2, 6, 23, 0.65)",
};

const sectionStyle = {
  marginTop: "18px",
  borderRadius: "16px",
  padding: "20px",
  background: "rgba(15, 23, 42, 0.85)",
  border: "1px solid rgba(148, 163, 184, 0.25)",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.1rem",
  fontWeight: 800,
  color: "#f8fafc",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.83rem",
  color: "rgba(226, 232, 240, 0.66)",
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
};

const labelStyle = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#e2e8f0",
};

const hintTextStyle = {
  fontSize: "0.78rem",
  color: "rgba(226, 232, 240, 0.6)",
  marginTop: "4px",
};

const chipWrapperStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  minHeight: "64px",
  padding: "10px",
  borderRadius: "12px",
  border: "1px solid rgba(148, 163, 184, 0.3)",
  background: "rgba(2, 6, 23, 0.6)",
};

const weekdayGridStyle = {
  marginTop: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "14px",
};

const weekdayCardStyle = {
  borderRadius: "12px",
  border: "1px solid rgba(148, 163, 184, 0.35)",
  padding: "12px",
  background: "rgba(2, 6, 23, 0.55)",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const weekdayInputStyle = {
  borderRadius: "10px",
  padding: "6px 10px",
  background: "rgba(15, 23, 42, 0.85)",
  border: "1px solid rgba(148, 163, 184, 0.4)",
  color: "#e2e8f0",
};

const toggleButtonBase = {
  borderRadius: "999px",
  border: "1px solid",
  padding: "8px 18px",
  fontSize: "0.85rem",
  fontWeight: 700,
  cursor: "pointer",
  transition: "transform 120ms ease, background 120ms ease, border-color 120ms ease",
};

const actionsRowStyle = {
  marginTop: "24px",
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const primaryButtonStyle = {
  borderRadius: "12px",
  border: "1px solid rgba(61, 182, 233, 0.8)",
  background: "linear-gradient(135deg, rgba(61, 182, 233, 0.8), rgba(34, 58, 122, 0.9))",
  color: "#eff6ff",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
};

const secondaryButtonStyle = {
  borderRadius: "12px",
  border: "1px solid rgba(248, 113, 113, 0.8)",
  background: "transparent",
  color: "#fecaca",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.92)",
  border: "1px solid rgba(148, 163, 184, 0.45)",
  color: "#e2e8f0",
  fontSize: "0.95rem",
};

const selectFieldStyle = {
  ...inputFieldStyle,
  appearance: "none",
};

const textareaFieldStyle = {
  width: "100%",
  minHeight: "110px",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.92)",
  border: "1px solid rgba(148, 163, 184, 0.45)",
  color: "#e2e8f0",
  fontSize: "0.95rem",
  resize: "vertical",
};

const statusBadgeStyle = {
  padding: "6px 14px",
  borderRadius: "999px",
  border: "1px solid rgba(61, 182, 233, 0.85)",
  color: "#c8f0f0",
  fontWeight: 700,
};

const muteBadgeStyle = {
  padding: "6px 14px",
  borderRadius: "999px",
  border: "1px solid rgba(248, 113, 113, 0.4)",
  color: "#fecaca",
  fontWeight: 700,
};

const chipTagStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  borderRadius: "999px",
  padding: "6px 10px",
  border: "1px solid rgba(34, 58, 122, 0.8)",
  color: "#d9f1fb",
  background: "rgba(15, 23, 42, 0.75)",
  fontSize: "0.85rem",
  fontWeight: 600,
};

const chipCloseStyle = {
  border: "none",
  background: "transparent",
  color: "#fecaca",
  fontSize: "1rem",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  lineHeight: 1,
};

const RoomRateForm = ({ businessUnitId, onSuccess, onCancel }) => {
  const { baseCurrency, currencyCatalog = [] } = useCurrencyDisplay();
  const [formData, setFormData] = useState({
    rate_code: "",
    name: "",
    client_id: "",
    room_category_id: "",
    room_category_ids: [],
    group_key: "",
    currency: "EUR",
    valid_from: "",
    valid_to: "",
    status: "active",
    adults: "",
    adults_match_type: "exact",

    children: "",
    children_match_type: "upto",

    max_occupancy: "",
    max_occupancy_match_type: "upto",
    is_refundable: true,

    // ✅ Booking Engine flag
    show_in_booking_engine: true,

    cancellation_policy: "",
    penalty_type: "",
    penalty_amount: "",
    penalty_description: "",

    // ✅ Rate price limits + default stays (RoomRates columns)
    min_price_amount: "",
    max_price_amount: "",
    default_min_stay: "",
    default_max_stay: "",
    use_stay_defaults_by_weekday: false,
    default_min_stay_by_weekday: emptyWeekdayValues(),
    default_max_stay_by_weekday: emptyWeekdayValues(),
  });

  useEffect(() => {
    setFormData((current) => ({ ...current, currency: baseCurrency || "EUR" }));
  }, [baseCurrency]);



  const [clients, setClients] = useState([]);
  const [roomCategories, setRoomCategories] = useState([]);
  const [capacityLimits, setCapacityLimits] = useState({ adults: null, children: null, max_occupancy: null }); // [RatesForm] Derived limits

  useEffect(() => {
    // Cargar categorías de habitación
    axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`)
      .then(res => {
        const filtered = res.data.filter(
          (cat) => Number(cat.business_unit_id) === Number(businessUnitId)
        );
        setRoomCategories(filtered);
      })
      .catch(err => console.error("❌ Error cargando room_categories:", err));

    // Cargar clientes
    axios.get(`${process.env.REACT_APP_API_URL}/api/clients`)
      .then(res => setClients(res.data))
      .catch(err => console.error("❌ Error cargando clients:", err));
  }, [businessUnitId]);

  // [RatesForm] Recalculate capacity limits whenever room categories selection changes
  useEffect(() => {
    const selected = formData.room_category_ids;
    if (!Array.isArray(selected) || selected.length === 0) {
      setCapacityLimits({ adults: null, children: null, max_occupancy: null });
      setFormData(prev => ({ ...prev, adults: "", children: "", max_occupancy: "" })); // Reset to "Any"
      console.log("[RatesForm] Limits cleared (no categories). Reset adults/children to Any");
      return;
    }

    const selectedCats = roomCategories.filter(c => selected.includes(Number(c.id)));
    const numsAdults = selectedCats.map(c => Number(c.max_adults)).filter(n => !isNaN(n));
    const numsChildren = selectedCats.map(c => Number(c.max_children)).filter(n => !isNaN(n));

    const numsOcc = selectedCats
      .map(c => (c.max_occupancy === null || typeof c.max_occupancy === "undefined" ? null : Number(c.max_occupancy)))
      .filter(v => v !== null && !isNaN(v));

    let minAdults = numsAdults.length ? Math.min(...numsAdults) : 0;
    let minChildren = numsChildren.length ? Math.min(...numsChildren) : 0;

    // If categories have max_occupancy set, use the minimum across selected categories; otherwise null (no limit)
    let minOcc = numsOcc.length ? Math.min(...numsOcc) : null;

    if (!isFinite(minAdults)) minAdults = 0;
    if (!isFinite(minChildren)) minChildren = 0;
    if (minOcc !== null && !isFinite(minOcc)) minOcc = null;

    setCapacityLimits({ adults: minAdults, children: minChildren, max_occupancy: minOcc });
    // If current values exceed new limits, reset to "Any"
    setFormData(prev => {
      const next = { ...prev };
      if (prev.adults !== "" && Number(prev.adults) > minAdults) next.adults = "";
      if (prev.children !== "" && Number(prev.children) > minChildren) next.children = "";
      if (minOcc !== null && prev.max_occupancy !== "" && Number(prev.max_occupancy) > minOcc) next.max_occupancy = "";
      return next;
    });

    console.log("[RatesForm] Limits recalculated → adults:", minAdults, "children:", minChildren, "| Selected categories:", selected);
  }, [formData.room_category_ids, roomCategories]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // When selecting a single category, append it as a chip and reset the select
    if (name === "room_category_id") {
      const id = Number(value);
      if (!isNaN(id) && id > 0) {
        setFormData(prev => {
          const already = prev.room_category_ids.includes(id);
          const next = already ? prev.room_category_ids : [...prev.room_category_ids, id];
          console.log("[RatesForm] Added category to chips:", id, "→", next);
          return {
            ...prev,
            room_category_ids: next,
            room_category_id: "" // reset selector to allow adding another
          };
        });
      } else {
        // keep it empty
        setFormData(prev => ({ ...prev, room_category_id: "" }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleWeekdayStayChange = (field, weekday, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [weekday]: value,
      },
    }));
  };

  const submitRoomRate = async () => {
    const precisionError = getRoomRateFormPrecisionError(formData, currencyCatalog);
    if (precisionError) {
      alert(precisionError);
      return;
    }
    const parsedDefaultMinStay =
      formData.default_min_stay === "" ? null : Number(formData.default_min_stay);
    const parsedDefaultMaxStay =
      formData.default_max_stay === "" ? null : Number(formData.default_max_stay);

    if (
      parsedDefaultMinStay !== null &&
      (!Number.isInteger(parsedDefaultMinStay) || parsedDefaultMinStay < 1)
    ) {
      alert("Default Min Stay must be an integer >= 1 (or empty).");
      return;
    }
    if (
      parsedDefaultMaxStay !== null &&
      (!Number.isInteger(parsedDefaultMaxStay) || parsedDefaultMaxStay < 1)
    ) {
      alert("Default Max Stay must be an integer >= 1 (or empty).");
      return;
    }

    const minByWeekdayResult = normalizeWeekdayPayload(formData.default_min_stay_by_weekday);
    const maxByWeekdayResult = normalizeWeekdayPayload(formData.default_max_stay_by_weekday);

    if (minByWeekdayResult.error) {
      alert(minByWeekdayResult.error);
      return;
    }
    if (maxByWeekdayResult.error) {
      alert(maxByWeekdayResult.error);
      return;
    }

    const useWeekdayDefaults = !!formData.use_stay_defaults_by_weekday;
    const minByWeekday = useWeekdayDefaults ? minByWeekdayResult.value : null;
    const maxByWeekday = useWeekdayDefaults ? maxByWeekdayResult.value : null;

    const stayValidationError = validateStayRanges({
      minDefault: parsedDefaultMinStay,
      maxDefault: parsedDefaultMaxStay,
      minByWeekday,
      maxByWeekday,
    });
    if (stayValidationError) {
      alert(stayValidationError);
      return;
    }

    try {
      // Base sanitization
      const base = {
        rate_code: formData.rate_code.trim(),
        name: formData.name.trim(),
        client_id: formData.client_id === "" ? null : Number(formData.client_id),
        currency: formData.currency.trim(),
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null,
        status: formData.status,
        adults: formData.adults === "" ? null : Number(formData.adults),
        adults_match_type: formData.adults === "" ? null : (formData.adults_match_type || "exact"),

        children: formData.children === "" ? null : Number(formData.children),
        children_match_type: formData.children === "" ? null : (formData.children_match_type || "upto"),

        max_occupancy: formData.max_occupancy === "" ? null : Number(formData.max_occupancy),
        max_occupancy_match_type:
          formData.max_occupancy === ""
            ? null
            : (formData.max_occupancy_match_type || "upto"),
        is_refundable: (formData.is_refundable === true || formData.is_refundable === "true"),

        // âœ… IMPORTANT: send boolean
        show_in_booking_engine: !!formData.show_in_booking_engine,

        cancellation_policy: formData.cancellation_policy || "",
        penalty_type: formData.penalty_type === "" ? null : formData.penalty_type,
        penalty_amount: formData.penalty_amount === "" ? null : Number(formData.penalty_amount),
        penalty_description: formData.penalty_description || "",

        // âœ… NEW: thresholds + default stays (empty -> null)
        min_price_amount: formData.min_price_amount === "" ? null : Number(formData.min_price_amount),
        max_price_amount: formData.max_price_amount === "" ? null : Number(formData.max_price_amount),
        default_min_stay: parsedDefaultMinStay,
        default_max_stay: parsedDefaultMaxStay,
        default_min_stay_by_weekday: minByWeekday,
        default_max_stay_by_weekday: maxByWeekday,
      };


      // [RatesForm] Defensive clamp based on current limits (Any â†’ null; out-of-range â†’ null)
      const limA = capacityLimits?.adults ?? 0;
      const limC = capacityLimits?.children ?? 0;

      if (base.adults !== null && limA > 0 && base.adults > limA) {
        console.warn("[RatesForm] Adults value exceeds limit; normalizing to Any", base.adults, ">", limA);
        base.adults = null;
      }
      if (base.children !== null && limC > 0 && base.children > limC) {
        console.warn("[RatesForm] Children value exceeds limit; normalizing to Any", base.children, ">", limC);
        base.children = null;
      }

      const limO = capacityLimits?.max_occupancy ?? null;
      if (base.max_occupancy !== null && limO !== null && limO > 0 && base.max_occupancy > limO) {
        console.warn("[RatesForm] max_occupancy exceeds limit; normalizing to Any", base.max_occupancy, ">", limO);
        base.max_occupancy = null;
      }

      let payload;


      // Ensure at least one category via chips (preferred UX)
      if (Array.isArray(formData.room_category_ids) && formData.room_category_ids.length > 0) {
        payload = {
          ...base,
          room_category_ids: formData.room_category_ids.map(Number),
        };
        if (formData.group_key && formData.group_key.trim() !== "") {
          payload.group_key = formData.group_key.trim();
        }
      } else if (formData.room_category_id) {
        // Fallback: user selected but didn't get added (shouldn't happen now)
        payload = {
          ...base,
          room_category_ids: [Number(formData.room_category_id)],
        };
      } else {
        alert("Please select at least one room category.");
        console.warn("[RatesForm] No categories selected - abort submit");
        return;
      }


      console.log("[RatesForm] Submit payload:", payload);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/roomRates`, payload);

      // If multi, backend may return { created_count, rates, group_key }
      console.log("[RatesForm] Server response:", response.data);

      const newId = response?.data?.id ?? response?.data?.rates?.[0]?.id ?? null;
      if (onSuccess) onSuccess(newId);

    } catch (error) {
      console.error("[RatesForm] Error creating Room Rate:", error);
      alert("There was an error creating the Room Rate. Please check the console.");
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitRoomRate();
  };


  const hasCategories = formData.room_category_ids.length > 0;
  const occupancySummary = hasCategories
    ? [
        capacityLimits.adults && capacityLimits.adults > 0
          ? `Adults up to ${capacityLimits.adults}`
          : "Adults unrestricted",
        typeof capacityLimits.children === "number" && capacityLimits.children >= 0
          ? `Children up to ${capacityLimits.children}`
          : "Children unrestricted",
        capacityLimits.max_occupancy !== null && capacityLimits.max_occupancy > 0
          ? `Max occupancy ${capacityLimits.max_occupancy}`
          : null,
      ]
        .filter(Boolean)
        .join(" \u00B7 ")
    : "Select categories to derive occupancy caps.";

  const weekdayToggleLabel = formData.use_stay_defaults_by_weekday
    ? "Use defaults across all weekdays"
    : "Define min/max stay per weekday";

  return (
    <div className="rm-roomRateFormOuter" style={{ padding: "18px 12px" }}>
      <div className="rm-roomRateForm" style={formShellStyle}>
        <div className="rm-roomRateFormHeader" style={{ textAlign: "center", marginBottom: "22px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "rgba(248, 250, 252, 0.65)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          >
            Room rate studio
          </p>
          <h2 style={{ margin: 0, fontSize: "2.05rem", color: "#f8fafc" }}>Create new room rate</h2>
          <p style={{ marginTop: "6px", color: "rgba(226, 232, 240, 0.75)" }}>
            Group categories, control occupancy and publish windows with a single panel.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <section className="rm-roomRateFormSection" style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={sectionTitleStyle}>Rate essentials</h3>
                <p style={sectionTextStyle}>Code, category set and currency that identify the rate.</p>
              </div>
              <span className={`rm-roomRateFormStatus${formData.status === "active" ? " is-active" : ""}`} style={formData.status === "active" ? statusBadgeStyle : muteBadgeStyle}>
                {formData.status === "active" ? "Active" : "Inactive"}
              </span>
            </div>
            <div style={fieldGridStyle}>
              <div>
                <label style={labelStyle}>Rate Code *</label>
                <input
                  type="text"
                  name="rate_code"
                  value={formData.rate_code}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  required
                  maxLength={6}
                />
              </div>
              <div>
                <label style={labelStyle}>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  required
                />
              </div>
              <div>
                <label style={labelStyle}>Room category</label>
                <select
                  name="room_category_id"
                  value={formData.room_category_id}
                  onChange={handleChange}
                  style={selectFieldStyle}
                >
                  <option value="">Select a category to add below</option>
                  {roomCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <p style={hintTextStyle}>Add categories one by one; they appear as chips below.</p>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Selected categories</label>
                <div className="rm-roomRateFormChips" style={chipWrapperStyle}>
                  {formData.room_category_ids.length === 0 && (
                    <span style={{ color: "rgba(226, 232, 240, 0.64)", fontSize: "0.85rem" }}>
                      No categories selected yet.
                    </span>
                  )}
                  {formData.room_category_ids.map((id) => {
                    const cat = roomCategories.find((c) => Number(c.id) === Number(id));
                    const label = cat ? cat.name : `#${id}`;
                    return (
                      <span key={id} className="rm-roomRateFormChip" style={chipTagStyle}>
                        {label}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => {
                              const next = prev.room_category_ids.filter((x) => Number(x) !== Number(id));
                              return { ...prev, room_category_ids: next };
                            });
                          }}
                          style={chipCloseStyle}
                          aria-label={`Remove ${label}`}
                          title="Remove"
                        >
                          {"\u00d7"}
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Group key (optional)</label>
                <input
                  type="text"
                  name="group_key"
                  value={formData.group_key}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  placeholder="Leave empty to auto-generate"
                />
                <p style={hintTextStyle}>Use to append new categories to an existing group.</p>
              </div>
              <div>
                <label style={labelStyle}>Client</label>
                <select name="client_id" value={formData.client_id} onChange={handleChange} style={selectFieldStyle}>
                  <option value="">None</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Currency</label>
                <input
                  type="text"
                  name="currency"
                  value={formData.currency}
                  readOnly
                  style={inputFieldStyle}
                />
                <p style={hintTextStyle}>PMS rates are always maintained in the hotel base currency.</p>
              </div>
            </div>
          </section>

          <section className="rm-roomRateFormSection" style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={sectionTitleStyle}>Visibility + channel</h3>
                <p style={sectionTextStyle}>Control what status and channels this rate is listed on.</p>
              </div>
            </div>
            <div style={fieldGridStyle}>
              <div>
                <label style={labelStyle}>Status</label>
                <select style={selectFieldStyle} name="status" value={formData.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <p style={hintTextStyle}>Inactive rates stay off the board until reactivated.</p>
              </div>
              <div>
                <label style={labelStyle}>Booking engine exposure</label>
                <button
                  type="button"
                  className={`rm-roomRateFormToggle${formData.show_in_booking_engine ? " is-active" : ""}`}
                  onClick={() => setFormData((prev) => ({ ...prev, show_in_booking_engine: !prev.show_in_booking_engine }))}
                  style={{
                    ...toggleButtonBase,
                    borderColor: formData.show_in_booking_engine ? "rgba(61, 182, 233, 0.8)" : "rgba(226, 232, 240, 0.4)",
                    background: formData.show_in_booking_engine ? "rgba(61, 182, 233, 0.18)" : "rgba(15, 23, 42, 0.7)",
                    color: formData.show_in_booking_engine ? "#d9f1fb" : "#e2e8f0",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  {formData.show_in_booking_engine ? "Visible in booking engine" : "Hidden from booking engine"}
                </button>
                <p style={hintTextStyle}>Toggle to keep the rate private or publish to guests.</p>
              </div>
            </div>
          </section>

          <section className="rm-roomRateFormSection" style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={sectionTitleStyle}>Occupancy & limits</h3>
                <p style={sectionTextStyle}>{occupancySummary}</p>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "16px" }}>
              <div>
                <label style={labelStyle}>Adults rule</label>
                <select
                  name="adults_match_type"
                  value={formData.adults_match_type}
                  onChange={handleChange}
                  style={selectFieldStyle}
                  disabled={formData.adults === ""}
                >
                  <option value="exact">Exact</option>
                  <option value="upto">Up to</option>
                </select>
                <p style={hintTextStyle}>Only active when a value is selected.</p>
              </div>
              <div>
                <label style={labelStyle}>Adults</label>
                <select
                  name="adults"
                  value={formData.adults}
                  onChange={handleChange}
                  style={selectFieldStyle}
                  disabled={!capacityLimits.adults || capacityLimits.adults <= 0}
                >
                  <option value="">Any</option>
                  {Array.from({ length: capacityLimits.adults || 0 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Children rule</label>
                <select
                  name="children_match_type"
                  value={formData.children_match_type}
                  onChange={handleChange}
                  style={selectFieldStyle}
                  disabled={formData.children === ""}
                >
                  <option value="upto">Up to</option>
                  <option value="exact">Exact</option>
                </select>
                <p style={hintTextStyle}>Applies only once children are explicitly set.</p>
              </div>
              <div>
                <label style={labelStyle}>Children</label>
                <select
                  name="children"
                  value={formData.children}
                  onChange={handleChange}
                  style={selectFieldStyle}
                  disabled={capacityLimits.children === null}
                >
                  <option value="">Any</option>
                  <option value={0}>0</option>
                  {Array.from({ length: capacityLimits.children || 0 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Max occupancy rule</label>
                <select
                  name="max_occupancy_match_type"
                  value={formData.max_occupancy_match_type}
                  onChange={handleChange}
                  style={selectFieldStyle}
                  disabled={formData.max_occupancy === ""}
                >
                  <option value="upto">Up to</option>
                  <option value="exact">Exact</option>
                </select>
                <p style={hintTextStyle}>Optional rules for the combined occupancy cap.</p>
              </div>
              <div>
                <label style={labelStyle}>Max occupancy</label>
                <select
                  name="max_occupancy"
                  value={formData.max_occupancy}
                  onChange={handleChange}
                  style={selectFieldStyle}
                  disabled={capacityLimits.max_occupancy === null}
                >
                  <option value="">Any</option>
                  {Array.from({ length: capacityLimits.max_occupancy || 0 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="rm-roomRateFormSection" style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={sectionTitleStyle}>Price & duration</h3>
                <p style={sectionTextStyle}>Reflect floor/ceiling values and standard lengths.</p>
              </div>
            </div>
            <div style={fieldGridStyle}>
              <div>
                <label style={labelStyle}>Minimum price</label>
                <input
                  type="number"
                  step={currencyAmountStep(formData.currency, currencyCatalog)}
                  min="0"
                  name="min_price_amount"
                  value={formData.min_price_amount}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  placeholder="No limit"
                />
                <p style={hintTextStyle}>Rate prices will not dip below this.</p>
              </div>
              <div>
                <label style={labelStyle}>Maximum price</label>
                <input
                  type="number"
                  step={currencyAmountStep(formData.currency, currencyCatalog)}
                  min="0"
                  name="max_price_amount"
                  value={formData.max_price_amount}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  placeholder="No limit"
                />
                <p style={hintTextStyle}>Rate prices will not exceed this.</p>
              </div>
              <div>
                <label style={labelStyle}>Default min stay</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  name="default_min_stay"
                  value={formData.default_min_stay}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  placeholder="No default"
                />
              </div>
              <div>
                <label style={labelStyle}>Default max stay</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  name="default_max_stay"
                  value={formData.default_max_stay}
                  onChange={handleChange}
                  style={inputFieldStyle}
                  placeholder="No default"
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <button
                  type="button"
                  className={`rm-roomRateFormToggle${formData.use_stay_defaults_by_weekday ? " is-active" : ""}`}
                  onClick={() => {
                    setFormData((prev) => {
                      if (prev.use_stay_defaults_by_weekday) {
                        return { ...prev, use_stay_defaults_by_weekday: false };
                      }

                      const minByWeekday = { ...prev.default_min_stay_by_weekday };
                      const maxByWeekday = { ...prev.default_max_stay_by_weekday };

                      for (const day of WEEKDAY_OPTIONS) {
                        if (minByWeekday[day.key] === "" && prev.default_min_stay !== "") {
                          minByWeekday[day.key] = String(prev.default_min_stay);
                        }
                        if (maxByWeekday[day.key] === "" && prev.default_max_stay !== "") {
                          maxByWeekday[day.key] = String(prev.default_max_stay);
                        }
                      }

                      return {
                        ...prev,
                        use_stay_defaults_by_weekday: true,
                        default_min_stay_by_weekday: minByWeekday,
                        default_max_stay_by_weekday: maxByWeekday,
                      };
                    });
                  }}
                  style={{
                    ...toggleButtonBase,
                    borderColor: formData.use_stay_defaults_by_weekday
                      ? "rgba(34, 58, 122, 0.8)"
                      : "rgba(148, 163, 184, 0.7)",
                    background: formData.use_stay_defaults_by_weekday ? "rgba(34, 58, 122, 0.25)" : "rgba(15, 23, 42, 0.75)",
                    color: formData.use_stay_defaults_by_weekday ? "#d9f1fb" : "#e2e8f0",
                    width: "max(220px, 40%)",
                  }}
                >
                  {weekdayToggleLabel}
                </button>
                <p style={hintTextStyle}>Enable per-weekday min/max stays for granular control.</p>
              </div>
            </div>

            {formData.use_stay_defaults_by_weekday && (
              <div style={weekdayGridStyle}>
                {WEEKDAY_OPTIONS.map((day) => (
                  <div key={day.key} className="rm-roomRateFormWeekday" style={weekdayCardStyle}>
                    <span style={{ fontWeight: 700, color: "#d9f1fb" }}>{day.label}</span>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      placeholder="Min"
                      value={formData.default_min_stay_by_weekday[day.key]}
                      onChange={(e) =>
                        handleWeekdayStayChange("default_min_stay_by_weekday", day.key, e.target.value)
                      }
                      style={weekdayInputStyle}
                    />
                    <input
                      type="number"
                      step="1"
                      min="1"
                      placeholder="Max"
                      value={formData.default_max_stay_by_weekday[day.key]}
                      onChange={(e) =>
                        handleWeekdayStayChange("default_max_stay_by_weekday", day.key, e.target.value)
                      }
                      style={weekdayInputStyle}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rm-roomRateFormSection" style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <div>
                <h3 style={sectionTitleStyle}>Policies & timeline</h3>
                <p style={sectionTextStyle}>Define validity windows, cancellation rules and penalties.</p>
              </div>
            </div>
            <div style={fieldGridStyle}>
              <div>
                <label style={labelStyle}>Valid from *</label>
                <input type="date" name="valid_from" value={formData.valid_from} onChange={handleChange} style={inputFieldStyle} required />
              </div>
              <div>
                <label style={labelStyle}>Valid to</label>
                <input type="date" name="valid_to" value={formData.valid_to} onChange={handleChange} style={inputFieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Refundable</label>
                <select name="is_refundable" value={formData.is_refundable} onChange={handleChange} style={selectFieldStyle}>
                  <option value={true}>Yes</option>
                  <option value={false}>No</option>
                </select>
                <p style={hintTextStyle}>Choose yes to allow refunds for this rate.</p>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Cancellation policy</label>
                <textarea name="cancellation_policy" value={formData.cancellation_policy} onChange={handleChange} style={textareaFieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Penalty type</label>
                <select name="penalty_type" value={formData.penalty_type} onChange={handleChange} style={selectFieldStyle}>
                  <option value="">None</option>
                  <option value="fixed">Fixed</option>
                  <option value="percent">Percent</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Penalty amount</label>
                <input type="number" step={formData.penalty_type === "fixed" ? currencyAmountStep(formData.currency, currencyCatalog) : "0.01"} name="penalty_amount" value={formData.penalty_amount} onChange={handleChange} style={inputFieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Penalty description</label>
                <input type="text" name="penalty_description" value={formData.penalty_description} onChange={handleChange} style={inputFieldStyle} />
              </div>
            </div>
          </section>

          <div className="rm-roomRateFormActions" style={actionsRowStyle}>
            <button type="submit" style={primaryButtonStyle}>
              Create rate
            </button>

            {onCancel && (
              <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default RoomRateForm;
